// React imports
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Third-party imports
import Konva from 'konva';
import type { Box } from 'konva/lib/shapes/Transformer';
import { Circle, Label, Layer, Line, Tag, Text, Transformer } from 'react-konva';
import { useShallow } from 'zustand/react/shallow';

// Internal imports
import { useMagicPenStore } from '@src/stores';
import { useRoomContext } from '@src/contexts';

// Hooks imports
import {
  usePenTool,
  useLineTool,
  useTextTool,
  useShapeTool,
  useEraserTool,
  useEventHandlers,
  useObjectRenderer,
  useStickyNoteTool,
  useObjectInteraction,
  useMagicPenIntegration,
  useTextEditing,
} from './hooks';

import {
  constrainResizeDimensions,
  createBoundBoxFunc,
  getColorPropertyForShape,
  getMinDimensions,
  toAbsoluteCanvasObject,
} from '../helpers';

// Constants imports
import {
  DEFAULT_STROKE_WIDTH,
  ERASER_CLEANUP_INTERVAL_MS,
  ERASER_DASH_PATTERN,
  ERASER_FADE_DURATION_MS,
  ERASER_STROKE_COLOR,
  ERASER_STROKE_OPACITY,
  ERASER_STROKE_WIDTH,
  FADE_DURATION,
  FADE_START_TIME,
  LINE_PREVIEW_DASH,
  LINE_START_INDICATOR_RADIUS,
  LINE_START_INDICATOR_STROKE_WIDTH,
  LINE_TENSION,
  MIN_SHAPE_SIZE_TO_RENDER,
  TOOLTIP_FONT_SIZE,
  TOOLTIP_OFFSET,
  TOOLTIP_PADDING,
  TOOLTIP_SHADOW_BLUR,
  TOOLTIP_SHADOW_OFFSET_X,
  TOOLTIP_SHADOW_OFFSET_Y,
  TOOLTIP_SHADOW_OPACITY,
  TOOLTIP_TAG_POINTER_HEIGHT,
  TOOLTIP_TAG_POINTER_WIDTH,
  STICKER_FONT_SIZE,
} from '../magicPen.constants';

import { EMagicPenShape, ICanvasObject, ICreatedBy, IEditingStickyNotes } from '@src/types';

const useMagicPenCanvas = (canAnnotate: boolean = true, containerWidth: number, containerHeight: number) => {
  // ===== REFS =====
  const layerRef = useRef<Konva.Layer>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const lastColorTriggerRef = useRef<number>(0);

  // ===== LOCAL STATE =====
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [editingStickyNote, setEditingStickyNote] = useState<IEditingStickyNotes | null>(null);

  // ===== STORE STATE =====
  const {
    objects,
    hoveredId,
    brushColor,
    selectedId,
    activeTool,
    currentShape,
    lineStartPoint,
    currentLinePoints,
    eraserStrokes = [],
    colorUpdateTrigger,
    setObjects,
    setEraserStrokes,
  } = useMagicPenStore(
    useShallow((state) => ({
      objects: state.objects,
      hoveredId: state.hoveredId,
      brushColor: state.brushColor,
      activeTool: state.activeTool,
      selectedId: state.selectedId,
      currentShape: state.currentShape,
      eraserStrokes: state.eraserStrokes,
      lineStartPoint: state.lineStartPoint,
      currentLinePoints: state.currentLinePoints,
      colorUpdateTrigger: state.colorUpdateTrigger,
      setObjects: state.setObjects,
      setEraserStrokes: state.setEraserStrokes,
    }))
  );

  // ===== CONTEXT =====
  const { currentPeer } = useRoomContext();

  // ===== CREATED BY OBJECT =====
  const createdBy: ICreatedBy = useMemo(
    () => ({
      peer_id: currentPeer?.peer_info?.peer_id || 'unknown',
      peer_uuid: currentPeer?.peer_info?.peer_uuid || 'unknown',
      peer_name: currentPeer?.peer_info?.peer_name || 'Unknown User',
      record_id: currentPeer?.record_id || 'unknown',
    }),
    [currentPeer]
  );

  // ===== COMPUTED VALUES =====
  const hoveredObject = useMemo(() => objects.find((obj) => obj.id === hoveredId), [objects, hoveredId]);
  const selectedObject = useMemo(() => objects.find((obj) => obj.id === selectedId), [objects, selectedId]);

  // ===== HOOKS =====
  const { handleAddObjects, emitAddAnnotation, emitUpdateAnnotation, emitDeleteAnnotation } = useMagicPenIntegration(
    containerWidth,
    containerHeight
  );

  const { handleEraserLogic, handleEraserClick, getEraserRefs } = useEraserTool(
    createdBy.peer_id,
    emitDeleteAnnotation
  );

  const { handlePenMouseUp } = usePenTool(createdBy, handleAddObjects);

  const { handleShapeMouseMove, handleShapeMouseUp, startShapeDrawing } = useShapeTool(createdBy, handleAddObjects);

  const { handleTextDblClick, handleTextClick, createNewText, createSticker } = useTextTool(
    setIsEditingText,
    createdBy,
    handleAddObjects,
    emitAddAnnotation,
    emitUpdateAnnotation
  );
  const {
    textareaRef,
    textareaValue,
    setTextareaValue,
    handleTextEditComplete,
    handleTextEditCancel,
    handleTextSelection,
  } = useTextEditing({
    editingStickyNote,
    setEditingStickyNote,
    emitAddAnnotation,
    emitUpdateAnnotation,
  });

  const { createStickyNote } = useStickyNoteTool(setIsDrawing, setEditingStickyNote, editingStickyNote, createdBy);

  const { handleLineMouseMove, handleLineClick } = useLineTool(createdBy, handleAddObjects);

  const { handleObjectClick, handleObjectHover, handleObjectUnhover, handleStageClick } = useObjectInteraction({
    stageRef,
    currentCreatedById: createdBy.peer_id,
    canAnnotate,
    emitUpdateAnnotation,
  });

  const { renderObject } = useObjectRenderer({
    isEditingText,
    handleObjectClick,
    handleObjectHover,
    handleObjectUnhover,
    handleTextClick,
    handleTextDblClick,
    stageRef,
    setEditingStickyNote,
    editingStickyNote,
    setTooltipPos,
    handleEraserClick,
    currentCreatedById: createdBy.peer_id,
    canAnnotate,
    emitUpdateAnnotation,
  });

  // Create a wrapper for handleStageClick that matches the Konva event signature
  const handleStageClickWrapper = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Call the original handleStageClick with the required parameters
      return handleStageClick(e, isEditingText);
    },
    [handleStageClick, isEditingText]
  );

  // ===== MOUSE EVENT HANDLERS =====
  // Remove the original event handlers and use the ones from the hook
  const { handleMouseDown, handleMouseMove, handleMouseUp } = useEventHandlers({
    isDrawing,
    setIsDrawing,
    isEditingText,
    handleEraserLogic,
    handleLineMouseMove,
    handleShapeMouseMove,
    handlePenMouseUp,
    handleShapeMouseUp,
    handleLineClick,
    getEraserRefs,
    startShapeDrawing,
    createSticker,
    createNewText,
    createStickyNote,
    stageRef,
  });

  const handleMouseMoveWithTooltip = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      handleMouseMove(e);

      // Update tooltip position when hovering over an object (allow in select mode or none mode for viewers)
      if (hoveredId && (activeTool === 'select' || activeTool === 'none')) {
        const stage = e.target.getStage();
        if (stage) {
          const pointerPos = stage.getPointerPosition();
          if (pointerPos) {
            setTooltipPos({
              x: pointerPos.x + TOOLTIP_OFFSET.x,
              y: pointerPos.y + TOOLTIP_OFFSET.y,
            });
          }
        }
      } else {
        setTooltipPos(null);
      }
    },
    [handleMouseMove, hoveredId, activeTool]
  );

  // Global mouseup listener to catch mouse releases outside the canvas
  // Only attach when drawing to minimize overhead
  useEffect(() => {
    if (!isDrawing) return;

    const handleGlobalMouseUp = () => {
      handleMouseUp();
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDrawing, handleMouseUp]);

  useEffect(() => {
    if (selectedId && transformerRef.current && stageRef.current) {
      const stage = stageRef.current;
      const transformer = transformerRef.current;
      const selectedNode = stage.findOne(`#${selectedId}`);

      if (selectedNode) {
        transformer.nodes([selectedNode]);
        transformer.getLayer()?.batchDraw();

        // Add transformer change handler for resizable objects
        const handleTransformEnd = () => {
          // Use getState() to get latest objects without triggering re-render (was facing infinite rendering issue)
          const currentObjects = useMagicPenStore.getState().objects;
          const selectedObject = currentObjects.find((obj) => obj.id === selectedId);

          if (!selectedObject) {
            return;
          }

          const node = selectedNode;
          const stage = stageRef.current;

          // Get the current scale
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          // Get current position (after transform)
          let currentX = node.x();
          let currentY = node.y();

          // Handle different shape types
          if (selectedObject.type === EMagicPenShape.STICKY_NOTE) {
            const defaultWidth = 100;
            const defaultHeight = 60;
            const minWidth = 80;
            const minHeight = 50;
            const padding = 10;

            // Calculate new dimensions based on scale
            const calculatedWidth = (selectedObject.width || defaultWidth) * scaleX;
            const calculatedHeight = (selectedObject.height || defaultHeight) * scaleY;

            // Use helper to constrain dimensions within canvas bounds
            let { width: newWidth, height: newHeight } = constrainResizeDimensions(
              { x: currentX, y: currentY },
              calculatedWidth,
              calculatedHeight,
              selectedObject.type,
              stage
            );

            // Ensure dimensions never go to 0 (prevents canvas drawing error)
            newWidth = Math.max(minWidth, newWidth);
            newHeight = Math.max(minHeight, newHeight);

            // Constrain position to prevent going off canvas (all 4 edges)
            if (stage) {
              const stageWidth = stage.width();
              const stageHeight = stage.height();
              currentX = Math.max(0, Math.min(currentX, stageWidth - newWidth));
              currentY = Math.max(0, Math.min(currentY, stageHeight - newHeight));
            }

            // Temporarily detach transformer to avoid interference
            transformer.nodes([]);

            // Reset scale and update position
            node.scaleX(1);
            node.scaleY(1);
            node.x(currentX);
            node.y(currentY);

            // Calculate new font size proportionally for sticky notes
            const averageScale = (scaleX + scaleY) / 2;
            const newFontSize = Math.max(8, Math.min(32, (selectedObject.fontSize || 14) * averageScale));

            // Update the Group's children dimensions
            const group = node as Konva.Group;
            const rect = group.findOne('Rect');
            const text = group.findOne('Text');
            if (rect) {
              rect.width(newWidth);
              rect.height(newHeight);
            }
            if (text) {
              text.x(padding);
              text.y(padding);
              text.width(Math.max(newWidth - padding * 2, 0));
              text.height(Math.max(newHeight - padding * 2, 0));
            }
            group.getClientRect();

            const updatedObjects = currentObjects.map((obj) =>
              obj.id === selectedId
                ? {
                    ...obj,
                    x: currentX,
                    y: currentY,
                    width: newWidth,
                    height: newHeight,
                    fontSize: newFontSize,
                    rotation: node.rotation(),
                    // Convert absolute coordinates to relative coordinates
                    relativeX: currentX / containerWidth,
                    relativeY: currentY / containerHeight,
                    relativeWidth: newWidth / containerWidth,
                    relativeHeight: newHeight / containerHeight,
                  }
                : obj
            );

            setObjects(updatedObjects);

            const updatedObject = updatedObjects.find((obj) => obj.id === selectedId);
            if (updatedObject && selectedObject.createdBy?.peer_id === createdBy.peer_id) {
              void emitUpdateAnnotation([updatedObject]);
            }

            transformer.nodes([node]);
            transformer.getLayer()?.batchDraw();
            return;
          }

          if (selectedObject.type === EMagicPenShape.STICKER) {
            const textNode = node as Konva.Text;
            const baseFontSize = selectedObject.fontSize || STICKER_FONT_SIZE;
            const newFontSize = Math.max(8, baseFontSize * scaleY);
            const horizontalScale = scaleY !== 0 ? scaleX / scaleY : scaleX;

            transformer.nodes([]);
            textNode.fontSize(newFontSize);
            textNode.scaleX(1);
            textNode.scaleY(1);

            const baseRect = textNode.getSelfRect();
            const baseWidth = Math.max(baseRect.width, getMinDimensions(EMagicPenShape.STICKER).width);
            const baseHeight = Math.max(baseRect.height, getMinDimensions(EMagicPenShape.STICKER).height);
            const measuredWidth = baseWidth * horizontalScale;
            const measuredHeight = baseHeight;

            if (stage) {
              const stageWidth = stage.width();
              const stageHeight = stage.height();
              currentX = Math.max(0, Math.min(currentX, stageWidth - measuredWidth));
              currentY = Math.max(0, Math.min(currentY, stageHeight - measuredHeight));
            }

            textNode.x(currentX);
            textNode.y(currentY);
            textNode.scaleX(horizontalScale);
            textNode.scaleY(1);

            const updatedObjects = currentObjects.map((obj) =>
              obj.id === selectedId
                ? {
                    ...obj,
                    x: currentX,
                    y: currentY,
                    width: measuredWidth,
                    height: measuredHeight,
                    fontSize: newFontSize,
                    scaleX: horizontalScale,
                    scaleY: 1,
                    rotation: textNode.rotation(),
                  }
                : obj
            );

            setObjects(updatedObjects);

            const updatedObject = updatedObjects.find((obj) => obj.id === selectedId);
            if (updatedObject && selectedObject.createdBy?.peer_id === createdBy.peer_id) {
              void emitUpdateAnnotation([updatedObject]);
            }

            transformer.nodes([node]);
            transformer.getLayer()?.batchDraw();
            return;
          }

          if (selectedObject.type === EMagicPenShape.TEXT_BOX) {
            const defaultWidth = 200;
            const defaultHeight = 40;
            const minWidth = 100;
            const minHeight = 40;

            const calculatedWidth = (selectedObject.width || defaultWidth) * scaleX;
            const calculatedHeight = (selectedObject.height || defaultHeight) * scaleY;

            let { width: newWidth, height: newHeight } = constrainResizeDimensions(
              { x: currentX, y: currentY },
              calculatedWidth,
              calculatedHeight,
              selectedObject.type,
              stage
            );

            newWidth = Math.max(minWidth, newWidth);
            newHeight = Math.max(minHeight, newHeight);

            if (stage) {
              const stageWidth = stage.width();
              const stageHeight = stage.height();
              currentX = Math.max(0, Math.min(currentX, stageWidth - newWidth));
              currentY = Math.max(0, Math.min(currentY, stageHeight - newHeight));
            }

            transformer.nodes([]);

            node.scaleX(1);
            node.scaleY(1);
            node.x(currentX);
            node.y(currentY);

            const textNode = node as Konva.Text;
            textNode.width(newWidth);
            textNode.height(newHeight);

            const updatedObjects = currentObjects.map((obj) =>
              obj.id === selectedId
                ? {
                    ...obj,
                    x: currentX,
                    y: currentY,
                    width: newWidth,
                    height: newHeight,
                    rotation: textNode.rotation(),
                  }
                : obj
            );

            setObjects(updatedObjects);

            const updatedObject = updatedObjects.find((obj) => obj.id === selectedId);
            if (updatedObject && selectedObject.createdBy?.peer_id === createdBy.peer_id) {
              void emitUpdateAnnotation([updatedObject]);
            }

            transformer.nodes([node]);
            transformer.getLayer()?.batchDraw();
            return;
          }

          if (
            [EMagicPenShape.RECTANGLE, EMagicPenShape.CIRCLE, EMagicPenShape.LINE, EMagicPenShape.ARROW].includes(
              selectedObject.type
            )
          ) {
            // Handle shapes: rectangle, circle, line, arrow (pen and ephemeral pen create LINE objects)
            // Reset scale and update dimensions in store to match current scaled size
            const minDimensions = getMinDimensions(selectedObject.type);

            if (selectedObject.type === EMagicPenShape.RECTANGLE) {
              // Rectangle: update width and height
              const rectNode = node as Konva.Rect;
              const currentWidth = rectNode.width() * scaleX;
              const currentHeight = rectNode.height() * scaleY;

              const newWidth = Math.max(minDimensions.width, currentWidth);
              const newHeight = Math.max(minDimensions.height, currentHeight);

              // Constrain position
              if (stage) {
                const stageWidth = stage.width();
                const stageHeight = stage.height();
                currentX = Math.max(0, Math.min(currentX, stageWidth - newWidth));
                currentY = Math.max(0, Math.min(currentY, stageHeight - newHeight));
              }

              transformer.nodes([]);
              rectNode.scaleX(1);
              rectNode.scaleY(1);
              rectNode.width(newWidth);
              rectNode.height(newHeight);
              rectNode.x(currentX);
              rectNode.y(currentY);

              const updatedRectangleObjects = currentObjects.map((obj) =>
                obj.id === selectedId
                  ? {
                      ...obj,
                      x: currentX,
                      y: currentY,
                      width: newWidth,
                      height: newHeight,
                      rotation: rectNode.rotation(),
                      // Convert absolute coordinates to relative coordinates
                      relativeX: currentX / containerWidth,
                      relativeY: currentY / containerHeight,
                      relativeWidth: newWidth / containerWidth,
                      relativeHeight: newHeight / containerHeight,
                    }
                  : obj
              );

              setObjects(updatedRectangleObjects);

              const updatedRectangle = updatedRectangleObjects.find((obj) => obj.id === selectedId);
              if (updatedRectangle && selectedObject.createdBy?.peer_id === createdBy.peer_id) {
                void emitUpdateAnnotation([updatedRectangle]);
              }

              transformer.nodes([rectNode]);
              transformer.getLayer()?.batchDraw();
            } else if (selectedObject.type === EMagicPenShape.CIRCLE) {
              // Circle: update radius (stored as width/height)
              const circleNode = node as Konva.Circle;
              const currentRadius = circleNode.radius() * Math.max(scaleX, scaleY);
              const newRadius = Math.max(minDimensions.width / 2, currentRadius);
              const newSize = newRadius * 2;

              // Constrain position
              if (stage) {
                const stageWidth = stage.width();
                const stageHeight = stage.height();
                currentX = Math.max(newRadius, Math.min(currentX, stageWidth - newRadius));
                currentY = Math.max(newRadius, Math.min(currentY, stageHeight - newRadius));
              }

              transformer.nodes([]);
              circleNode.scaleX(1);
              circleNode.scaleY(1);
              circleNode.radius(newRadius);
              circleNode.x(currentX);
              circleNode.y(currentY);

              const updatedCircleObjects = currentObjects.map((obj) =>
                obj.id === selectedId
                  ? {
                      ...obj,
                      x: currentX,
                      y: currentY,
                      width: newSize,
                      height: newSize,
                      rotation: circleNode.rotation(),
                      // Convert absolute coordinates to relative coordinates
                      relativeX: currentX / containerWidth,
                      relativeY: currentY / containerHeight,
                      relativeWidth: newSize / containerWidth,
                      relativeHeight: newSize / containerHeight,
                    }
                  : obj
              );

              setObjects(updatedCircleObjects);

              const updatedCircle = updatedCircleObjects.find((obj) => obj.id === selectedId);
              if (updatedCircle && selectedObject.createdBy?.peer_id === createdBy.peer_id) {
                void emitUpdateAnnotation([updatedCircle]);
              }

              transformer.nodes([circleNode]);
              transformer.getLayer()?.batchDraw();
            } else if (selectedObject.type === EMagicPenShape.ARROW) {
              // Arrow: update width and height while preserving angle/direction
              const arrowNode = node as Konva.Arrow;
              const currentPoints = arrowNode.points();

              if (currentPoints && currentPoints.length >= 4) {
                // Arrow points are [0, 0, width, height]
                // Get original width and height from points (before scaling)
                const originalWidth = currentPoints[2];
                const originalHeight = currentPoints[3];

                // Calculate original angle and length
                const originalAngle = Math.atan2(originalHeight, originalWidth);
                const originalLength = Math.sqrt(originalWidth * originalWidth + originalHeight * originalHeight);

                // Calculate minimum length (hypotenuse of minimum dimensions)
                const minLength = Math.sqrt(
                  minDimensions.width * minDimensions.width + minDimensions.height * minDimensions.height
                );

                // Use average scale to preserve angle while scaling magnitude
                const avgScale = (scaleX + scaleY) / 2;
                const newLength = Math.max(minLength, originalLength * avgScale);

                // Calculate new width and height preserving the angle
                let newWidth = newLength * Math.cos(originalAngle);
                let newHeight = newLength * Math.sin(originalAngle);

                // Ensure minimum dimensions are met while preserving direction
                if (Math.abs(newWidth) < minDimensions.width || Math.abs(newHeight) < minDimensions.height) {
                  // If either dimension is too small, scale up proportionally
                  const scaleFactor = Math.max(
                    minDimensions.width / Math.abs(newWidth || 1),
                    minDimensions.height / Math.abs(newHeight || 1)
                  );
                  newWidth = newWidth * scaleFactor;
                  newHeight = newHeight * scaleFactor;
                }

                const finalWidth = newWidth;
                const finalHeight = newHeight;

                // Constrain position - account for arrows pointing in any direction
                if (stage) {
                  const stageWidth = stage.width();
                  const stageHeight = stage.height();

                  // Calculate bounding box of arrow (from 0,0 to width,height)
                  const minX = Math.min(0, finalWidth);
                  const maxX = Math.max(0, finalWidth);
                  const minY = Math.min(0, finalHeight);
                  const maxY = Math.max(0, finalHeight);

                  // Constrain position so arrow stays within canvas
                  currentX = Math.max(-minX, Math.min(currentX, stageWidth - maxX));
                  currentY = Math.max(-minY, Math.min(currentY, stageHeight - maxY));
                }

                transformer.nodes([]);
                arrowNode.scaleX(1);
                arrowNode.scaleY(1);
                // Update arrow points: [0, 0, width, height] preserving angle
                arrowNode.points([0, 0, finalWidth, finalHeight]);
                arrowNode.x(currentX);
                arrowNode.y(currentY);

                const updatedArrowObjects = currentObjects.map((obj) =>
                  obj.id === selectedId
                    ? {
                        ...obj,
                        x: currentX,
                        y: currentY,
                        width: finalWidth,
                        height: finalHeight,
                        rotation: arrowNode.rotation(),
                        // Convert absolute coordinates to relative coordinates
                        relativeX: currentX / containerWidth,
                        relativeY: currentY / containerHeight,
                        relativeWidth: finalWidth / containerWidth,
                        relativeHeight: finalHeight / containerHeight,
                      }
                    : obj
                );

                setObjects(updatedArrowObjects);

                const updatedArrow = updatedArrowObjects.find((obj) => obj.id === selectedId);
                if (updatedArrow && selectedObject.createdBy?.peer_id === createdBy.peer_id) {
                  void emitUpdateAnnotation([updatedArrow]);
                }

                transformer.nodes([arrowNode]);
                transformer.getLayer()?.batchDraw();
              }
            } else if (selectedObject.type === EMagicPenShape.LINE) {
              // Line (pen and ephemeral pen create LINE objects): update points based on absolute transform
              const lineNode = node as Konva.Line;
              const currentPoints = lineNode.points();

              if (currentPoints && currentPoints.length >= 2) {
                // Normalized pen/line resize & rotation math so strokes behave like rectangles: clamp center inside stage,
                // convert points to center-relative coordinates, store rotation separately, and keep relativePoints stable during drag.
                // Prevents jumping, preserves sizing across peers, and keeps Konva transforms in sync with our persisted geometry.
                const rotationDegrees = lineNode.rotation();
                const rotationRadians = (rotationDegrees * Math.PI) / 180;
                const cosRotation = Math.cos(rotationRadians);
                const sinRotation = Math.sin(rotationRadians);

                const absoluteTransform = lineNode.getAbsoluteTransform().copy();
                const absolutePoints: number[] = [];

                for (let i = 0; i < currentPoints.length; i += 2) {
                  const transformedPoint = absoluteTransform.point({
                    x: currentPoints[i],
                    y: currentPoints[i + 1],
                  });
                  absolutePoints.push(transformedPoint.x, transformedPoint.y);
                }

                let minX = absolutePoints[0];
                let maxX = absolutePoints[0];
                let minY = absolutePoints[1];
                let maxY = absolutePoints[1];

                for (let i = 0; i < absolutePoints.length; i += 2) {
                  minX = Math.min(minX, absolutePoints[i]);
                  maxX = Math.max(maxX, absolutePoints[i]);
                  minY = Math.min(minY, absolutePoints[i + 1]);
                  maxY = Math.max(maxY, absolutePoints[i + 1]);
                }

                const width = Math.max(1, maxX - minX);
                const height = Math.max(1, maxY - minY);

                const originalCenterX = (minX + maxX) / 2;
                const originalCenterY = (minY + maxY) / 2;

                let finalCenterX = originalCenterX;
                let finalCenterY = originalCenterY;

                if (stage) {
                  const stageWidth = stage.width();
                  const stageHeight = stage.height();
                  const halfWidth = width / 2;
                  const halfHeight = height / 2;

                  if (stageWidth > 0) {
                    const minCenterX = Math.min(Math.max(halfWidth, 0), stageWidth);
                    const maxCenterX = Math.max(stageWidth - halfWidth, minCenterX);
                    finalCenterX = Math.min(Math.max(finalCenterX, minCenterX), maxCenterX);
                  }

                  if (stageHeight > 0) {
                    const minCenterY = Math.min(Math.max(halfHeight, 0), stageHeight);
                    const maxCenterY = Math.max(stageHeight - halfHeight, minCenterY);
                    finalCenterY = Math.min(Math.max(finalCenterY, minCenterY), maxCenterY);
                  }
                }

                const normalizedPoints: number[] = [];
                for (let i = 0; i < absolutePoints.length; i += 2) {
                  const relX = absolutePoints[i] - finalCenterX;
                  const relY = absolutePoints[i + 1] - finalCenterY;

                  // Rotate points back into the line's unrotated local space
                  const unrotatedX = relX * cosRotation + relY * sinRotation;
                  const unrotatedY = -relX * sinRotation + relY * cosRotation;

                  normalizedPoints.push(unrotatedX, unrotatedY);
                }

                const normalizedRelativePoints = normalizedPoints.map((point, index) => {
                  const size = index % 2 === 0 ? containerWidth : containerHeight;
                  return size !== 0 ? point / size : 0;
                });

                transformer.nodes([]);
                lineNode.scaleX(1);
                lineNode.scaleY(1);
                lineNode.offsetX(0);
                lineNode.offsetY(0);
                lineNode.points(normalizedPoints);
                lineNode.x(finalCenterX);
                lineNode.y(finalCenterY);
                lineNode.rotation(rotationDegrees);

                const updatedObjects = currentObjects.map((obj) =>
                  obj.id === selectedId
                    ? {
                        ...obj,
                        x: finalCenterX,
                        y: finalCenterY,
                        points: normalizedPoints,
                        relativePoints: normalizedRelativePoints,
                        rotation: rotationDegrees,
                        // Convert absolute coordinates to relative coordinates
                        relativeX: containerWidth !== 0 ? finalCenterX / containerWidth : 0,
                        relativeY: containerHeight !== 0 ? finalCenterY / containerHeight : 0,
                      }
                    : obj
                );

                setObjects(updatedObjects);

                const updatedObject = updatedObjects.find((obj) => obj.id === selectedId);
                if (updatedObject && selectedObject.createdBy?.peer_id === createdBy.peer_id) {
                  void emitUpdateAnnotation([updatedObject]);
                }

                transformer.nodes([lineNode]);
                transformer.getLayer()?.batchDraw();
              }

              return;
            }

            transformer.nodes([node]);
            transformer.getLayer()?.batchDraw();
            return;
          }

          // Pure rotation case: no resize branch triggered above
          transformer.nodes([]);
          const rotatedObjects = currentObjects.map((obj) =>
            obj.id === selectedId ? { ...obj, rotation: node.rotation() } : obj
          );
          setObjects(rotatedObjects);
          const rotatedObject = rotatedObjects.find((obj) => obj.id === selectedId);
          if (rotatedObject && selectedObject.createdBy?.peer_id === createdBy.peer_id) {
            void emitUpdateAnnotation([rotatedObject]);
          }

          transformer.nodes([node]);
          transformer.getLayer()?.batchDraw();
        };

        transformer.on('transformend', handleTransformEnd);

        return () => {
          transformer.off('transformend', handleTransformEnd);
        };
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }
  }, [containerHeight, containerWidth, selectedId, setObjects, emitUpdateAnnotation, createdBy.peer_id]); // Removed 'objects' from dependencies to prevent infinite loop

  // Handle ephemeral objects (disappearing ink) fade and removal
  useEffect(() => {
    const hasDeletingObjects = objects.some((obj) => obj.deletedAt);
    const hasEphemeralObjects = objects.some((obj) => obj.drawEndTime && obj.ttlMs);
    if (!hasDeletingObjects && !hasEphemeralObjects) return;

    const animationFrame = requestAnimationFrame(function redraw() {
      layerRef.current?.batchDraw();
      if (objects.some((obj) => obj.deletedAt || (obj.drawEndTime && obj.ttlMs))) {
        requestAnimationFrame(redraw);
      }
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [objects, layerRef]);

  // Handle ephemeral objects (disappearing ink) fade and removal
  useEffect(() => {
    const hasEphemeral = objects.some((obj) => obj.drawEndTime && obj.ttlMs && !obj.deletedAt);

    if (!hasEphemeral) {
      return; // skip creating the interval entirely if there are no ephemeral objects
    }
    const interval = setInterval(() => {
      const getObjects = () => {
        const now = Date.now();
        let hasChanges = false;
        const prevObjects = objects;

        const updatedObjects = prevObjects
          .map((obj) => {
            if (!obj.drawEndTime || !obj.ttlMs) return obj;

            const elapsedTime = now - obj.drawEndTime;

            if (elapsedTime >= obj.ttlMs) {
              hasChanges = true;
              return null;
            }

            if (elapsedTime >= FADE_START_TIME) {
              const fadeProgress = (elapsedTime - FADE_START_TIME) / FADE_DURATION;
              const newOpacity = Math.max(0, 1 - fadeProgress);
              if (obj.opacity !== newOpacity) {
                hasChanges = true;
                return { ...obj, opacity: newOpacity };
              }
            }

            return obj;
          })
          .filter((obj): obj is ICanvasObject => obj !== null);

        return hasChanges ? updatedObjects : prevObjects;
      };
      setObjects(getObjects());
    }, 50);

    return () => clearInterval(interval);
  }, [objects, setObjects]);

  // Cleanup interval for eraser strokes and deleted objects
  useEffect(() => {
    const hasDeleted = objects.some((obj) => obj.deletedAt);
    const hasEraserTrails = eraserStrokes.length > 0;

    if (!hasDeleted && !hasEraserTrails) {
      return; // skip creating the interval entirely if there are no deleted objects or eraser trails
    }
    const interval = setInterval(() => {
      const now = Date.now();
      // Remove old eraser strokes
      setEraserStrokes(eraserStrokes.filter((stroke) => now - stroke.createdAt < ERASER_FADE_DURATION_MS));
      // Remove fully faded objects (after fade animation completes)
      setObjects(objects.filter((obj) => !obj.deletedAt || now - obj.deletedAt < ERASER_FADE_DURATION_MS));
    }, ERASER_CLEANUP_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [objects, setObjects, setEraserStrokes, eraserStrokes]);

  // Handle color change for selected shape
  useEffect(() => {
    if (colorUpdateTrigger === lastColorTriggerRef.current) return;
    if (!selectedObject || activeTool !== 'select') return;

    // Determine color property based on object type
    const colorUpdate = getColorPropertyForShape(selectedObject.type, brushColor);

    if (!colorUpdate) return;

    // Update trigger reference only when we're actually making changes
    lastColorTriggerRef.current = colorUpdateTrigger;

    // Update objects with new color using latest state
    const currentObjects = useMagicPenStore.getState().objects;
    const updatedObjects = currentObjects.map((obj) => (obj.id === selectedId ? { ...obj, ...colorUpdate } : obj));
    setObjects(updatedObjects);

    const updatedObject = updatedObjects.find((obj) => obj.id === selectedId);
    if (updatedObject) {
      void emitUpdateAnnotation([updatedObject]);
    }
  }, [
    colorUpdateTrigger,
    selectedObject,
    activeTool,
    selectedId,
    brushColor,
    setObjects,
    emitUpdateAnnotation,
    createdBy.peer_id,
  ]);

  // Normalize objects when stage dimensions change (handles sudden resize and late-join scenarios)
  useEffect(() => {
    // Wait for valid stage dimensions
    if (containerWidth === 0 || containerHeight === 0) return;

    const currentObjects = useMagicPenStore.getState().objects;
    if (currentObjects.length === 0) return;

    // Check if any objects need normalization:
    // 1. Missing absolute coordinates when relative ones exist (late-join scenario)
    // 2. Objects with relative coordinates should always have their absolutes recalculated on resize
    const requiresNormalization = currentObjects.some((obj) => {
      // If object has relative coordinates, we need to ensure absolutes match current stage size
      const hasRelativeCoords = obj.relativeX !== undefined || obj.relativeY !== undefined;
      const hasRelativeDims = obj.relativeWidth !== undefined || obj.relativeHeight !== undefined;
      const hasRelativePoints = obj.relativePoints && obj.relativePoints.length > 0;

      if (!hasRelativeCoords && !hasRelativeDims && !hasRelativePoints) {
        // Object has no relative data - skip normalization
        return false;
      }

      // Check if absolutes are missing or need recalculation
      const missingAbsoluteX = obj.relativeX !== undefined && obj.x === undefined;
      const missingAbsoluteY = obj.relativeY !== undefined && obj.y === undefined;
      const missingAbsoluteWidth = obj.relativeWidth !== undefined && obj.width === undefined;
      const missingAbsoluteHeight = obj.relativeHeight !== undefined && obj.height === undefined;
      const missingPoints = hasRelativePoints && (!obj.points || obj.points.length === 0);

      // If any absolutes are missing, definitely normalize
      if (missingAbsoluteX || missingAbsoluteY || missingAbsoluteWidth || missingAbsoluteHeight || missingPoints) {
        return true;
      }

      // If we have relative coordinates, verify absolutes match current stage size
      // This handles the case where stage resized but absolutes are stale
      if (hasRelativeCoords) {
        const expectedX = obj.relativeX! * containerWidth;
        const expectedY = obj.relativeY! * containerHeight;
        // Use a small epsilon to account for floating point precision
        const epsilon = 0.5;
        if (Math.abs((obj.x || 0) - expectedX) > epsilon || Math.abs((obj.y || 0) - expectedY) > epsilon) {
          return true;
        }
      }

      if (hasRelativeDims) {
        const expectedWidth = obj.relativeWidth! * containerWidth;
        const expectedHeight = obj.relativeHeight! * containerHeight;
        const epsilon = 0.5;
        if (
          Math.abs((obj.width || 0) - expectedWidth) > epsilon ||
          Math.abs((obj.height || 0) - expectedHeight) > epsilon
        ) {
          return true;
        }
      }

      return false;
    });

    if (!requiresNormalization) return;

    // Recalculate absolute geometry from relative coordinates using current stage size
    // This ensures objects stay in correct positions after resize
    const normalizedObjects = currentObjects.map((obj) => toAbsoluteCanvasObject(obj, containerWidth, containerHeight));

    setObjects(normalizedObjects);
  }, [containerWidth, containerHeight, setObjects]);

  // ===== RENDER FUNCTIONS =====
  const renderLayer = useCallback(
    () => (
      <Layer ref={layerRef}>
        {/* Render all objects sorted by z-index (lower z-index rendered first) */}
        {objects
          .slice() // Create a copy to avoid mutating the original array
          .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
          .map(renderObject)}

        {/* Current drawing line (freehand or line preview) */}
        {currentLinePoints.length > 0 && activeTool !== 'eraser' && (
          <Line
            points={currentLinePoints}
            stroke={brushColor}
            strokeWidth={DEFAULT_STROKE_WIDTH}
            tension={0.5}
            lineCap="round"
            lineJoin="round"
            dash={lineStartPoint ? LINE_PREVIEW_DASH : undefined}
          />
        )}
        {/* Current eraser stroke */}
        {isDrawing && currentLinePoints.length > 0 && activeTool === 'eraser' && (
          <Line
            points={currentLinePoints}
            stroke={ERASER_STROKE_COLOR}
            strokeWidth={ERASER_STROKE_WIDTH}
            tension={0.5}
            lineCap="round"
            lineJoin="round"
            dash={ERASER_DASH_PATTERN}
            opacity={ERASER_STROKE_OPACITY}
          />
        )}

        {eraserStrokes.map((stroke) => {
          const age = Date.now() - stroke.createdAt;
          const opacity = Math.max(0, 1 - age / ERASER_FADE_DURATION_MS);

          return (
            <Line
              key={stroke.id}
              points={stroke.points}
              stroke={ERASER_STROKE_COLOR}
              strokeWidth={ERASER_STROKE_WIDTH}
              tension={LINE_TENSION}
              lineCap="round"
              lineJoin="round"
              dash={ERASER_DASH_PATTERN}
              opacity={opacity * ERASER_STROKE_OPACITY}
            />
          );
        })}
        {/* Current shape preview while drawing - only show if shape has dimensions */}
        {currentShape &&
          Math.abs(currentShape.width || 0) > MIN_SHAPE_SIZE_TO_RENDER &&
          Math.abs(currentShape.height || 0) > MIN_SHAPE_SIZE_TO_RENDER &&
          renderObject(currentShape)}

        {/* Start point indicator for two-click line */}
        {lineStartPoint && (
          <Circle
            x={lineStartPoint.x}
            y={lineStartPoint.y}
            radius={LINE_START_INDICATOR_RADIUS}
            fill={brushColor}
            stroke={brushColor}
            strokeWidth={LINE_START_INDICATOR_STROKE_WIDTH}
          />
        )}

        {/* Transformer for selected object - hide when editing text or when user can't annotate */}
        {selectedId && !isEditingText && canAnnotate && (
          <Transformer
            ref={transformerRef}
            rotateEnabled={selectedObject?.type !== EMagicPenShape.STICKY_NOTE}
            enabledAnchors={
              selectedObject?.type === EMagicPenShape.CIRCLE
                ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
                : undefined
            }
            boundBoxFunc={
              selectedObject?.type === EMagicPenShape.STICKER
                ? (oldBox: Box, newBox: Box) => {
                    // For stickers, use actual client rect to get accurate visual bounds
                    const stage = stageRef.current;
                    if (!stage) return newBox;

                    const node = stage.findOne(`#${selectedId}`);
                    if (!node) return newBox;

                    const clientRect = node.getClientRect();
                    const nodeX = node.x();
                    const nodeY = node.y();

                    // Calculate offset between node position and visual bounds
                    const offsetX = clientRect.x - nodeX;
                    const offsetY = clientRect.y - nodeY;

                    // Adjust newBox to account for visual bounds offset
                    const adjustedX = newBox.x - offsetX;
                    const adjustedY = newBox.y - offsetY;

                    // Check bounds with adjusted position
                    const stageWidth = stage.width();
                    const stageHeight = stage.height();

                    const wouldExceedLeft = adjustedX < 0;
                    const wouldExceedTop = adjustedY < 0;
                    const wouldExceedRight = adjustedX + newBox.width > stageWidth;
                    const wouldExceedBottom = adjustedY + newBox.height > stageHeight;

                    if (wouldExceedLeft || wouldExceedTop || wouldExceedRight || wouldExceedBottom) {
                      return oldBox;
                    }

                    return newBox;
                  }
                : createBoundBoxFunc(selectedObject?.type, stageRef.current)
            }
          />
        )}

        {/* Tooltip showing creator name on hover (allow in select mode or none mode for viewers) */}
        {tooltipPos && hoveredObject && (activeTool === 'select' || activeTool === 'none') && (
          <Label x={tooltipPos.x} y={tooltipPos.y}>
            <Tag
              fill="#131d22"
              pointerDirection="down"
              pointerWidth={TOOLTIP_TAG_POINTER_WIDTH}
              pointerHeight={TOOLTIP_TAG_POINTER_HEIGHT}
              lineJoin="round"
              shadowColor="black"
              shadowBlur={TOOLTIP_SHADOW_BLUR}
              shadowOffsetX={TOOLTIP_SHADOW_OFFSET_X}
              shadowOffsetY={TOOLTIP_SHADOW_OFFSET_Y}
              shadowOpacity={TOOLTIP_SHADOW_OPACITY}
            />
            <Text
              text={`Created by: ${hoveredObject.createdBy.peer_name}`}
              fontSize={TOOLTIP_FONT_SIZE}
              padding={TOOLTIP_PADDING}
              fill="#ffffff"
              fontFamily="Arial"
            />
          </Label>
        )}
      </Layer>
    ),
    [
      objects,
      renderObject,
      currentLinePoints,
      activeTool,
      brushColor,
      lineStartPoint,
      isDrawing,
      eraserStrokes,
      currentShape,
      selectedId,
      isEditingText,
      tooltipPos,
      hoveredObject,
      selectedObject,
      canAnnotate,
    ]
  );

  // ===== RETURN VALUES =====
  return {
    stageRef,
    activeTool,
    handleMouseDown,
    handleMouseUp,
    handleStageClick: handleStageClickWrapper, // Use the wrapper instead
    handleMouseMoveWithTooltip,
    renderLayer,
    editingStickyNote,
    handleTextEditComplete,
    handleTextEditCancel,
    handleTextSelection,
    objects,
    setEditingStickyNote,
    textareaRef,
    textareaValue,
    setTextareaValue,
  };
};

export default useMagicPenCanvas;
